import path from 'path';

import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Dispatch } from 'redux';

import { MediaItem } from '@app/components/Media/types';
import secureRequest from '@app/helpers/secure-request';

import { doRequest } from './api';
import { CHAT_SEND_FILE_CLEAR, UPDATE_MEDIA, UPDATE_VIRUS_SCAN_STATUS } from '../constants/actions';
import { GET_SIGNED_URL_FOR_MEDIA_UPLOAD, SEND_MEDIA_TO_VERISK, GET_PUBLIC_SIGNED_URL_FOR_MEDIA_UPLOAD } from '../constants/endpoints';

export const updateMediaItem = (mediaItem: MediaItem) => ({
  type: UPDATE_MEDIA,
  payload: mediaItem,
});

export function clearFileUploadStatus() {
  return { type: CHAT_SEND_FILE_CLEAR };
}

let uploadCanceler: AbortController | null = null;

export const cancelUpload = () => (dispatch: Dispatch) => {
  if (uploadCanceler) {
    uploadCanceler.abort();
    uploadCanceler = null;
  }
  dispatch(clearFileUploadStatus());
};

// uploadToPublicBucket gives us an upload link to an expiring public bucket, needed for outbound customer media
// for profile pictures, which use a non-expiring private bucket, set uploadToPublicBucket to false
export const uploadMedia = async (
  file: File,
  validFileTypes: string[] = [],
  fileSizeLimitInMegaBytes = 25,
  uploadToPublicBucket = false,
) => {
  const filename = file?.name || '';
  const ext = path.extname(filename);
  uploadCanceler = new AbortController();

  // Check file type before attempting to create signed url
  if (!validFileTypes.includes(file?.type)) {
    throw new Error('Invalid file type');
  }

  // Get a signed url from the server
  let signedPOSTUrl;
  let endpoint;
  if (uploadToPublicBucket) {
    endpoint = `${GET_PUBLIC_SIGNED_URL_FOR_MEDIA_UPLOAD(ext, file?.type, fileSizeLimitInMegaBytes)}`;
  } else {
    endpoint = `${GET_SIGNED_URL_FOR_MEDIA_UPLOAD(ext, file?.type, fileSizeLimitInMegaBytes)}`;
  }
  try {
    signedPOSTUrl = await doRequest({
      endpoint,
      method: 'GET',
    });
  } catch (e: unknown) {
    throw new Error(String(e));
  }

  // Format file and signature into a form to POST
  const s3url = signedPOSTUrl?.url;
  const fields = signedPOSTUrl?.fields || {};
  const form = new FormData();
  Object.entries(fields).forEach(([field, value]) => {
    form.append(field, value as string);
  });
  form.append('Content-Type', file?.type);
  form.append('file', file);

  // Upload file
  const res = await axios.post(s3url, form, {
    validateStatus: (status: number) => status < 500,
    ...(uploadCanceler?.signal && { signal: uploadCanceler.signal }),
  });
  if (res?.status === 400) {
    const parser = new DOMParser();
    const xmlDOM = parser.parseFromString(res?.data, 'text/xml');
    throw new Error(xmlDOM?.getElementsByTagName('Code')?.[0]?.innerHTML);
  }
  return { key: fields.key, url: s3url };
};

export const updateVirusScanStatus = (
  fileUrl: string,
  virusScanStatus: string,
) => async (dispatch: Dispatch) => {
  dispatch({
    type: UPDATE_VIRUS_SCAN_STATUS,
    payload: { fileUrl, virusScanStatus },
  });
};

export const sendMediaToVerisk = createAsyncThunk(
  'media/sendMediaToVerisk',
  async ({ jobId, mediaMessageData }: {
    jobId: string; mediaMessageData: Array<{ fileName: string; messageId: string }> }) => {
    const args = {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ mediaMessageData }),
    };
    const response = await secureRequest(SEND_MEDIA_TO_VERISK(jobId), args);
    if (response.ok) {
      const json = await response.json();
      return json;
    }
    throw new Error('Failed to send media to Verisk');
  },
);
